'use client'

import { useCallback } from 'react'
import type { Table as TanStackTable } from '@tanstack/react-table'
import type { ColumnDef } from '../types'
import { EXPORT_CONFIG } from '../config'

interface UseCustomTableLogicProps {
  data: any[]
  columnsDef: ColumnDef[]
  table: TanStackTable<any>
  onExportCSV?: (data: any[]) => void
  onExportExcel?: (data: any[]) => void
}

export function useCustomTableLogic({
  data,
  columnsDef,
  table,
  onExportCSV,
  onExportExcel,
}: UseCustomTableLogicProps) {
  const getVisibleData = useCallback(() => {
    const rows = table.getFilteredRowModel().rows
    return rows.map(row => row.original)
  }, [table])

  const handleExportCSV = useCallback(() => {
    const visibleData = getVisibleData()

    if (onExportCSV) {
      onExportCSV(visibleData)
      return
    }

    const headers = columnsDef.filter(c => !c.hidden).map(c => c.header)
    const keys = columnsDef.filter(c => !c.hidden).map(c => c.accessorKey)

    const csvContent = [
      headers.join(EXPORT_CONFIG.csvDelimiter),
      ...visibleData.map(row =>
        keys.map(key => {
          const value = row[key]
          if (value === null || value === undefined) return ''
          const str = String(value)
          if (str.includes(EXPORT_CONFIG.csvDelimiter) || str.includes('\n') || str.includes(EXPORT_CONFIG.csvEnclosure)) {
            return `${EXPORT_CONFIG.csvEnclosure}${str.replace(/"/g, '""')}${EXPORT_CONFIG.csvEnclosure}`
          }
          return str
        }).join(EXPORT_CONFIG.csvDelimiter)
      )
    ].join('\n')

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `export_${new Date().toISOString().split('T')[0]}.csv`
    link.click()
    URL.revokeObjectURL(link.href)
  }, [getVisibleData, columnsDef, onExportCSV])

  const handleExportExcel = useCallback(() => {
    const visibleData = getVisibleData()

    if (onExportExcel) {
      onExportExcel(visibleData)
      return
    }

    handleExportCSV()
  }, [getVisibleData, onExportExcel, handleExportCSV])

  return {
    getVisibleData,
    handleExportCSV,
    handleExportExcel,
  }
}
